const STORAGE_KEY = 'crikket-launcher-position';
const DRAG_THRESHOLD_PX = 5;
const EDGE_GAP_PX = 8;

interface Position {
  left: number;
  top: number;
}

function readStoredPosition(): Position | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'left' in parsed &&
      'top' in parsed &&
      typeof parsed.left === 'number' &&
      typeof parsed.top === 'number'
    ) {
      return { left: parsed.left, top: parsed.top };
    }
    return null;
  } catch {
    return null;
  }
}

function storePosition(position: Position): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch {
    return;
  }
}

function clamp(position: Position, launcher: HTMLElement): Position {
  const maxLeft = window.innerWidth - launcher.offsetWidth - EDGE_GAP_PX;
  const maxTop = window.innerHeight - launcher.offsetHeight - EDGE_GAP_PX;
  return {
    left: Math.min(Math.max(EDGE_GAP_PX, position.left), Math.max(EDGE_GAP_PX, maxLeft)),
    top: Math.min(Math.max(EDGE_GAP_PX, position.top), Math.max(EDGE_GAP_PX, maxTop)),
  };
}

function place(launcher: HTMLElement, position: Position): void {
  const clamped = clamp(position, launcher);
  launcher.style.left = `${clamped.left}px`;
  launcher.style.top = `${clamped.top}px`;
  launcher.style.right = 'auto';
  launcher.style.bottom = 'auto';
}

function makeDraggable(launcher: HTMLElement, shadowRoot: ShadowRoot): void {
  launcher.style.touchAction = 'none';
  launcher.style.cursor = 'grab';

  const stored = readStoredPosition();
  if (stored) place(launcher, stored);

  let start: { pointerX: number; pointerY: number; left: number; top: number } | null = null;
  let dragged = false;

  launcher.addEventListener('pointerdown', (event) => {
    const rect = launcher.getBoundingClientRect();
    start = { pointerX: event.clientX, pointerY: event.clientY, left: rect.left, top: rect.top };
    dragged = false;
    launcher.setPointerCapture(event.pointerId);
  });

  launcher.addEventListener('pointermove', (event) => {
    if (!start) return;
    const dx = event.clientX - start.pointerX;
    const dy = event.clientY - start.pointerY;
    if (!dragged && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    dragged = true;
    launcher.style.cursor = 'grabbing';
    launcher.style.transition = 'none';
    place(launcher, { left: start.left + dx, top: start.top + dy });
  });

  const endDrag = (event: PointerEvent) => {
    if (!start) return;
    start = null;
    launcher.style.cursor = 'grab';
    launcher.style.transition = '';
    if (launcher.hasPointerCapture(event.pointerId)) {
      launcher.releasePointerCapture(event.pointerId);
    }
    if (dragged) {
      const rect = launcher.getBoundingClientRect();
      storePosition({ left: rect.left, top: rect.top });
    }
  };
  launcher.addEventListener('pointerup', endDrag);
  launcher.addEventListener('pointercancel', endDrag);

  shadowRoot.addEventListener(
    'click',
    (event) => {
      if (!dragged) return;
      dragged = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true,
  );

  window.addEventListener('resize', () => {
    if (launcher.style.left) {
      place(launcher, {
        left: launcher.getBoundingClientRect().left,
        top: launcher.getBoundingClientRect().top,
      });
    }
  });
}

export function createDraggableCrikketMount(): HTMLElement {
  const container = document.createElement('div');
  document.body.appendChild(container);

  const enhanced = new WeakSet<HTMLElement>();
  const enhance = () => {
    for (const host of Array.from(container.children)) {
      const shadowRoot = host.shadowRoot;
      const launcher = shadowRoot?.querySelector<HTMLElement>('.capture-launcher');
      if (shadowRoot && launcher && !enhanced.has(launcher)) {
        enhanced.add(launcher);
        makeDraggable(launcher, shadowRoot);
      }
    }
  };

  const observer = new MutationObserver(() => {
    enhance();
    for (const host of Array.from(container.children)) {
      if (host.shadowRoot) observer.observe(host.shadowRoot, { childList: true, subtree: true });
    }
  });
  observer.observe(container, { childList: true });

  return container;
}
